import { t } from "@lingui/macro";
import React from "react";
import Svg, { Circle } from "react-native-svg";

import { WhooshThemeColors } from "../../../theme/restyle/theme";
import { IndicatorTooltip } from "../../overlay/Tooltip/IndicatorTooltip/IndicatorTooltip";
import { Box, Text } from "../../primitive";

type AttendeeCircleProps = {
  maxSize: number;
  currentKnownClients: number;
  currentUnknownClients: number;
  showTooltip?: boolean;
  direction?: "top" | "bottom" | "left" | "right";
  isPublic?: boolean;
  playerNounOverride?: string;
};

type PercentageCircleProps = {
  size: number;
  percentages: {
    member: number;
    guest: number;
  };
};

const PercentageCircle = ({ size, percentages }: PercentageCircleProps) => {
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  const memberLength = ((100 - percentages.member) / 100) * circumference;
  const guestLength = ((100 - percentages.guest) / 100) * circumference;

  const BASE_MEMBER_DEGREE_ROTATION = -90;
  const getGuestStartingRotationDegree = () => {
    const WHOLE_CIRCLE_DEGREES = 360;
    const guestDegreeOffset = percentages.member * (WHOLE_CIRCLE_DEGREES / 100);
    return BASE_MEMBER_DEGREE_ROTATION + guestDegreeOffset;
  };

  // Used actual color strings for fill, stroke was not recognizing the tokens
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background Circle */}
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e1e5e5"
        strokeWidth={strokeWidth}
      />
      {/* Member Circle */}
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#1DAA75"
        strokeWidth={strokeWidth}
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={memberLength}
        transform={`rotate(${BASE_MEMBER_DEGREE_ROTATION} ${size / 2} ${
          size / 2
        })`}
      />
      {/* Guest Circle */}
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#FF784B"
        strokeWidth={strokeWidth}
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={guestLength}
        transform={`rotate(${getGuestStartingRotationDegree()} ${size / 2} ${
          size / 2
        })`}
      />
    </Svg>
  );
};

export const AttendeeCircle = ({
  maxSize,
  currentKnownClients,
  currentUnknownClients,
  isPublic,
  showTooltip = false,
  direction = "top",
  playerNounOverride,
}: AttendeeCircleProps) => {
  if (maxSize === 0) return null;
  const CIRCLE_SIZE_PX = 40;
  const memberPercentage = Math.ceil((currentKnownClients / maxSize) * 100);
  const guestPercentage = Math.ceil((currentUnknownClients / maxSize) * 100);

  const indicatorSets: {
    color: WhooshThemeColors;
    number: number;
    label: string;
  }[] = [
    {
      color: "icon-main-primary",
      number: currentKnownClients,
      label: isPublic ? t`${playerNounOverride ?? "Players"}` : t`Members`,
    },
  ];

  if (currentUnknownClients > 0) {
    indicatorSets.push({
      color: "icon-main-secondary",
      number: currentUnknownClients,
      label: isPublic
        ? t`Unknown ${playerNounOverride ?? "Players"}`
        : t`Guests`,
    });
  }

  const attendeeSlotsRemaining =
    maxSize - currentKnownClients - currentUnknownClients;

  if (attendeeSlotsRemaining > 0) {
    indicatorSets.push({
      color: "grayscale-500",
      number: attendeeSlotsRemaining,
      label: t`Free Spots`,
    });
  }

  return showTooltip ? (
    <IndicatorTooltip
      direction={direction}
      trigger={
        <Box
          width={40}
          height={40}
          position="relative"
          justifyContent="center"
          alignItems="center"
        >
          <PercentageCircle
            size={CIRCLE_SIZE_PX}
            percentages={{ member: memberPercentage, guest: guestPercentage }}
          />
          <Box
            position="absolute"
            justifyContent="center"
            alignItems="center"
            width={34}
            height={34}
            backgroundColor="surface-primary"
            style={{ borderRadius: 17 }}
          >
            <Text variant="mainHeading">
              {currentKnownClients + currentUnknownClients}
            </Text>
          </Box>
        </Box>
      }
      indicatorSets={indicatorSets}
    />
  ) : (
    <Box
      width={40}
      height={40}
      position="relative"
      justifyContent="center"
      alignItems="center"
    >
      <PercentageCircle
        size={CIRCLE_SIZE_PX}
        percentages={{ member: memberPercentage, guest: guestPercentage }}
      />
      <Box
        position="absolute"
        justifyContent="center"
        alignItems="center"
        width={34}
        height={34}
        backgroundColor="surface-primary"
        style={{ borderRadius: 17 }}
      >
        <Text variant="mainHeading">
          {currentKnownClients + currentUnknownClients}
        </Text>
      </Box>
    </Box>
  );
};
